import { useQuery } from '@tanstack/react-query'
import { axiosInstance } from '../utils/axios'

// Admin stats response from /api/admin/stats endpoint
interface AdminStats {
  devices: number
  locations: number
  users: number
  unitCategories: number
}

interface AdminStatsResponse {
  success: boolean
  data: AdminStats
}

/**
 * Fetch admin dashboard statistics from /api/admin/stats
 * Returns counts for devices, locations, users, and unit categories
 */
export const useAdminStatsQuery = () => {
  return useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: async () => {
      const response = await axiosInstance.get<AdminStatsResponse>('/admin/stats')
      return response.data.data
    },
  })
}

/**
 * Device stats derived from admin stats
 * Backward compatible with existing dashboard
 */
export const useAdminDeviceStatsQuery = () => {
  const { data, ...rest } = useAdminStatsQuery()
  return {
    ...rest,
    data: data ? { totalDevices: data.devices } : undefined,
  }
}

/**
 * Location stats derived from admin stats
 * Backward compatible with existing dashboard
 */
export const useAdminLocationStatsQuery = () => {
  const { data, ...rest } = useAdminStatsQuery()
  return {
    ...rest,
    data: data ? { totalLocations: data.locations } : undefined,
  }
}

/**
 * User stats derived from admin stats
 * Backward compatible with existing dashboard
 */
export const useAdminUserStatsQuery = () => {
  const { data, ...rest } = useAdminStatsQuery()
  return {
    ...rest,
    data: data ? { totalUsers: data.users } : undefined,
  }
}

/**
 * Unit category stats derived from admin stats
 * New hook for dashboard Unit Categories card
 */
export const useAdminUnitCategoriesStatsQuery = () => {
  const { data, ...rest } = useAdminStatsQuery()
  return {
    ...rest,
    data: data ? { totalCategories: data.unitCategories } : undefined,
  }
}
